/**
 * PrePostMeansChart.tsx — group mean at baseline vs endpoint for one outcome scale.
 *
 * Uses Victory (already a dependency — see WemwbsBarChart) so no new package.
 *
 * ANONYMITY (VER-109): this chart plots GROUP MEANS ONLY (two bars: baseline, endpoint)
 * with ±1 SD whiskers. It never renders individual participant scores — those live
 * exclusively in the raw CSV export. Do not add per-participant series here.
 */
import React, { FC } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { VictoryAxis, VictoryBar, VictoryChart, VictoryContainer, VictoryErrorBar, VictoryTheme } from "victory";
import { Descriptives } from "../../../client/api/survey";
import { verbalTheme } from "../../layout/themes/verbal.theme";

type PrePostMeansChartProps = {
    baseline: Descriptives;
    endpoint: Descriptives;
};

export const PrePostMeansChart: FC<PrePostMeansChartProps> = ({ baseline, endpoint }) => {
    const theme = useTheme();

    // Both means are required to draw a meaningful comparison.
    if (baseline.mean === null || endpoint.mean === null) {
        return null;
    }

    const colorBaseline = theme.palette.info.main; // blue — "before"
    const colorEndpoint = theme.palette.primary.main; // teal — "after"
    const fontFamily = verbalTheme.typography.fontFamily;

    const baselineSd = baseline.sd ?? 0;
    const endpointSd = endpoint.sd ?? 0;

    // Auto-scale the y-axis from the data (scales differ: WHO-5, Stirling, …).
    const upper = Math.max(baseline.mean + baselineSd, endpoint.mean + endpointSd);
    const yMax = upper <= 0 ? 1 : upper * 1.15;

    const labelStyle = { fontSize: 11, fontFamily, fill: theme.palette.text.primary };

    return (
        <Box>
            <Typography variant="body2" color="textSecondary" mb={1}>
                Group mean at baseline vs endpoint (whiskers show ±1 standard deviation).
            </Typography>
            <VictoryChart
                theme={VictoryTheme.material}
                containerComponent={<VictoryContainer responsive={true} />}
                width={480}
                height={240}
                domainPadding={{ x: 90 }}
                padding={{ top: 20, bottom: 40, left: 48, right: 24 }}
                domain={{ y: [0, yMax] }}
                categories={{ x: ["Baseline", "Endpoint"] }}
            >
                <VictoryAxis theme={VictoryTheme.material} style={{ tickLabels: { fontSize: 10, fontFamily } }} />
                <VictoryAxis
                    dependentAxis
                    theme={VictoryTheme.material}
                    style={{ tickLabels: { fontSize: 9, fontFamily } }}
                />
                <VictoryBar
                    data={[{ x: "Baseline", y: baseline.mean, label: baseline.mean.toFixed(1) }]}
                    barWidth={54}
                    cornerRadius={{ top: 4 }}
                    style={{ data: { fill: colorBaseline }, labels: labelStyle }}
                />
                <VictoryBar
                    data={[{ x: "Endpoint", y: endpoint.mean, label: endpoint.mean.toFixed(1) }]}
                    barWidth={54}
                    cornerRadius={{ top: 4 }}
                    style={{ data: { fill: colorEndpoint }, labels: labelStyle }}
                />
                <VictoryErrorBar
                    data={[
                        { x: "Baseline", y: baseline.mean, errorY: baselineSd },
                        { x: "Endpoint", y: endpoint.mean, errorY: endpointSd },
                    ]}
                    style={{ data: { stroke: theme.palette.text.secondary, strokeWidth: 1.5 } }}
                    borderWidth={6}
                />
            </VictoryChart>
        </Box>
    );
};
